import React, { Fragment, useState, useEffect } from 'react';
import { useTheme } from '@material-ui/styles';
import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
    Text,
    EditIcon,
    DeleteIcon,
    IconButton,
    OutlinedButton,
    ConfirmDialog,
    StyledCard,
    NoInformation,
    Progress,
    ContainedButton,
} from '../../index';

const Note = ({ data, onClickDelete, handleSave, disabled }) => {
    const [editing, setEditing] = useState(false);
    const [note, setNote] = useState({});

    useEffect(() => {
        setNote(data);
    }, [data]);

    const onClickEdit = () => {
        setEditing(true);
    };

    const handleNoteChange = (value) => {
        setNote({ ...note, description: value });
    };

    const handleCancel = () => {
        setEditing(false);
        setNote(data);
    };

    const onClickSave = () => {
        handleSave(note);
        setEditing(false);
    };

    return (
        <div
            style={{
                padding: '15px',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0px 2px 8px rgba(174, 180, 210, 0.4)',
                display: 'flex',
                flexDirection: 'column',
                margin: '5px 24px',
            }}
        >
            {note.mine && (
                <Text $color="gray2" $fontSize="12px">
                    {dayjs(note.createdAt).format('D MMM YYYY')}
                </Text>
            )}

            {editing ? (
                <TextField
                    id={`note-textfield-${note.id}`}
                    multiline
                    variant="outlined"
                    fullWidth
                    autoFocus
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                    }}
                    InputProps={{
                        style: {
                            borderRadius: '16px',
                        },
                    }}
                    value={note.description}
                    onChange={(e) => handleNoteChange(e.target.value)}
                    disabled={disabled}
                    inputProps={{ maxLength: 2000 }}
                />
            ) : (
                <Text style={{ marginTop: '10px' }}>{note?.description}</Text>
            )}
            <div style={{ display: 'flex', marginTop: '10px' }}>
                {note.mine ? (
                    <Fragment>
                        <Text $color="gray2" $fontSize="12px" style={{ flexGrow: 1 }}>
                            My Note
                        </Text>
                        <IconButton
                            style={{
                                marginLeft: '10px',
                                width: '16px',
                                height: '16px',
                            }}
                            onClick={onClickEdit}
                            disabled={disabled}
                        >
                            <EditIcon fill={editing ? 'pink' : 'black'} />
                        </IconButton>
                        <IconButton
                            style={{
                                marginLeft: '10px',
                                width: '16px',
                                height: '16px',
                            }}
                            onClick={() => onClickDelete(note.id)}
                            disabled={disabled}
                        >
                            <DeleteIcon fill="black" />
                        </IconButton>
                    </Fragment>
                ) : (
                    <Text $color="gray2" $fontSize="12px">
                        {note?.fromStaff?.fullname}
                    </Text>
                )}
            </div>
            {editing && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        columnGap: '25px',
                        justifyContent: 'flex-end',
                        marginTop: '10px',
                    }}
                >
                    <Text $color="gray2" onClick={handleCancel}>
                        Cancel
                    </Text>
                    <ContainedButton
                        onClick={onClickSave}
                        style={{
                            height: '24px',
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            width: 'auto',
                        }}
                    >
                        Save
                    </ContainedButton>
                </div>
            )}
        </div>
    );
};

Note.propTypes = {
    data: PropTypes.object,
    onClickDelete: PropTypes.func,
    handleSave: PropTypes.func,
};

export const PatientNoteCard = ({
    data,
    onChange,
    value,
    handleAddNote,
    handleDeleteNote,
    handleEditNote,
    loading,
    disabled,
}) => {
    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState('');

    const onClickDelete = (id) => {
        setOpen(true);
        setSelectedNote(id);
    };

    const onClickCancel = () => {
        setOpen(false);
        setSelectedNote('');
    };

    const onClickConfirm = () => {
        handleDeleteNote(selectedNote);
        onClickCancel();
    };

    return (
        <StyledCard
            style={{
                position: 'relative',
                padding: 0,
            }}
        >
            {loading && <Progress />}
            <ConfirmDialog
                open={open}
                contentText="Are you sure you want to delete this note ?"
                cancelText="Cancel"
                onCancel={onClickCancel}
                confirmText="Delete"
                onConfirm={onClickConfirm}
            />
            <div
                style={{
                    paddingTop: '24px',
                }}
            >
                <Text $fontSize="18px" style={{ marginLeft: '24px', marginRight: '24px' }}>
                    Note
                </Text>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '70vh',
                        overflowY: 'auto',
                        marginTop: '20px',
                    }}
                >
                    {data.length > 0 ? (
                        data.map((note) => (
                            <Note
                                key={note.id}
                                data={note}
                                onClickDelete={onClickDelete}
                                handleSave={handleEditNote}
                                disabled={disabled}
                            />
                        ))
                    ) : (
                        <NoInformation />
                    )}
                </div>
            </div>
            <div
                style={{
                    backgroundColor: theme.palette.common.white,
                    padding: ' 10px 24px 24px',
                }}
            >
                <TextField
                    id="patientNote"
                    multiline
                    minRows={3}
                    placeholder="Add note..."
                    fullWidth
                    InputProps={{
                        disableUnderline: true,
                    }}
                    inputProps={{
                        style: {
                            minHeight: '22px',
                            lineHeight: '20px',
                        },
                        maxLength: 2000,
                    }}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <OutlinedButton
                        $width="200px"
                        onClick={handleAddNote}
                        disabled={!value || disabled}
                    >
                        Add note
                    </OutlinedButton>
                </div>
            </div>
        </StyledCard>
    );
};

PatientNoteCard.propTypes = {
    data: PropTypes.array,
    value: PropTypes.string,
    onChange: PropTypes.func,
    handleAddNote: PropTypes.func,
    handleDeleteNote: PropTypes.func,
    handleEditNote: PropTypes.func,
    loading: PropTypes.bool,
};
